import React, { useEffect, useRef, useState } from "react";
import { cn } from "../../../lib/utils";
import { FOCUS_RING, FIELD } from "./classes";

/**
 * "Click any field to edit inline" (spec, Client Detail header).
 *
 * Shows the value as a button; a click turns it into an input. Enter or blur saves,
 * Escape cancels. `onSave(next)` returns a promise — the caller does the optimistic
 * update and the rollback; a rejection keeps the editor open with `error` shown under
 * it. `validate(next)` may return a message to refuse before any request. An unchanged
 * value closes without saving.
 */
export default function InlineEditableField({
  value, display, placeholder, onSave, validate, error, inputType = "text",
  className, inputClassName, label, testId, disabled = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [localError, setLocalError] = useState("");
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  useEffect(() => { if (!editing) setDraft(value || ""); }, [value, editing]);
  useEffect(() => { if (editing) input.current?.focus(); }, [editing]);

  const commit = async () => {
    if (busy) return;
    const next = draft.trim();
    if (next === (value || "").trim()) { setEditing(false); setLocalError(""); return; }
    const refusal = validate ? validate(next) : "";
    if (refusal) { setLocalError(refusal); return; }
    setBusy(true);
    try {
      await onSave(next);
      setEditing(false);
      setLocalError("");
    } catch {
      // The caller reports the failure (toast or `error`); the editor stays open.
    } finally {
      setBusy(false);
    }
  };

  const message = localError || error;
  if (!editing) {
    return (
      <button type="button" disabled={disabled} onClick={() => setEditing(true)} data-testid={testId} aria-label={label}
        className={cn("rounded-lg text-left hover:bg-eh-cream-deep disabled:cursor-default", FOCUS_RING, className)}>
        {display ?? (value || <span className="text-eh-muted">{placeholder}</span>)}
      </button>
    );
  }
  return (
    <span className="inline-flex flex-col">
      <input
        ref={input}
        type={inputType}
        value={draft}
        aria-label={label}
        aria-invalid={message ? true : undefined}
        disabled={busy}
        onChange={(e) => { setDraft(e.target.value); setLocalError(""); }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { e.preventDefault(); setEditing(false); setLocalError(""); }
        }}
        data-testid={testId ? `${testId}-input` : undefined}
        className={cn(FIELD, "py-1.5", message && "border-red-400", inputClassName)}
      />
      {message && <span role="alert" className="mt-1 text-xs text-red-700">{message}</span>}
    </span>
  );
}
